"use client";

import type { RefObject } from "react";
import { gsap, useGSAP } from "./gsap";

export type EyeRig = {
  /** viewBox width of the SVG the eyes live in. */
  viewWidth: number;
  /** Socket centres, in viewBox units. */
  sockets: { cx: number; cy: number }[];
  /** Where the pupils sit at rest, relative to the socket centre. */
  rest: { x: number; y: number };
  /** How far a pupil may travel from the socket centre. */
  reach: number;
};

const FULL_REACH_PX = 240; // pointer distance at which the pupils reach the socket edge

// Makes [data-pupil] circles inside the SVG follow the pointer (or glance around on touch screens),
// and blinks [data-lid] circles. Pupils must be rendered at socket centre + rest.
export function useWatchingEyes(svgRef: RefObject<SVGSVGElement | null>, rig: EyeRig, enabled = true) {
  useGSAP(
    () => {
      const svg = svgRef.current;
      if (!enabled || !svg) return;
      const pupils = gsap.utils.toArray<SVGElement>("[data-pupil]", svg);
      const lids = gsap.utils.toArray<SVGElement>("[data-lid]", svg);
      const mm = gsap.matchMedia();

      mm.add(
        { pointer: "(hover: hover) and (pointer: fine)", reduce: "(prefers-reduced-motion: reduce)" },
        (context, contextSafe) => {
          const { pointer, reduce } = context.conditions!;
          if (reduce) return;

          if (lids.length) gsap.set(lids, { autoAlpha: 1, scaleY: 0, transformOrigin: "50% 50%" });

          const moveX = pupils.map((p) => gsap.quickTo(p, "x", { duration: 0.6, ease: "power3" }));
          const moveY = pupils.map((p) => gsap.quickTo(p, "y", { duration: 0.6, ease: "power3" }));

          const look = (i: number, ox: number, oy: number) => {
            moveX[i]?.(ox - rig.rest.x);
            moveY[i]?.(oy - rig.rest.y);
          };
          const rest = () => rig.sockets.forEach((_, i) => look(i, rig.rest.x, rig.rest.y));

          const lookAt = (clientX: number, clientY: number) => {
            const box = svg.getBoundingClientRect();
            if (!box.width) return;
            const scale = box.width / rig.viewWidth;
            rig.sockets.forEach((s, i) => {
              const dx = clientX - (box.left + s.cx * scale);
              const dy = clientY - (box.top + s.cy * scale);
              const reach = rig.reach * Math.min(1, Math.hypot(dx, dy) / FULL_REACH_PX);
              const angle = Math.atan2(dy, dx);
              look(i, Math.cos(angle) * reach, Math.sin(angle) * reach);
            });
          };

          let blinkCall: gsap.core.Tween | undefined;
          const blink = contextSafe!(() => {
            const twice = Math.random() < 0.2;
            gsap
              .timeline()
              .to(lids, { scaleY: 1, duration: 0.07, ease: "power2.in" })
              .to(lids, { scaleY: 0, duration: 0.12, ease: "power2.out" })
              .to(lids, { scaleY: twice ? 1 : 0, duration: twice ? 0.07 : 0, ease: "power2.in" })
              .to(lids, { scaleY: 0, duration: twice ? 0.12 : 0, ease: "power2.out" });
            blinkCall = gsap.delayedCall(gsap.utils.random(2.5, 6), blink);
          });
          if (lids.length) blinkCall = gsap.delayedCall(gsap.utils.random(1, 2.5), blink);

          if (pointer) {
            const onMove = contextSafe!((e: PointerEvent) => lookAt(e.clientX, e.clientY)) as (e: PointerEvent) => void;
            const onLeave = contextSafe!((e: MouseEvent) => {
              if (!e.relatedTarget) rest();
            }) as (e: MouseEvent) => void;
            window.addEventListener("pointermove", onMove, { passive: true });
            document.addEventListener("mouseout", onLeave);
            return () => {
              blinkCall?.kill();
              window.removeEventListener("pointermove", onMove);
              document.removeEventListener("mouseout", onLeave);
            };
          }

          // Touch screens have no cursor to follow, so the eyes glance around on their own.
          let glanceCall: gsap.core.Tween | undefined;
          const glance = contextSafe!(() => {
            const angle = gsap.utils.random(0, Math.PI * 2);
            const reach = gsap.utils.random(0.5, 1) * rig.reach;
            rig.sockets.forEach((_, i) => look(i, Math.cos(angle) * reach, Math.sin(angle) * reach));
            glanceCall = gsap.delayedCall(gsap.utils.random(1.4, 3), glance);
          });
          glanceCall = gsap.delayedCall(0.8, glance);
          return () => {
            blinkCall?.kill();
            glanceCall?.kill();
          };
        },
      );

      return () => mm.revert();
    },
    { scope: svgRef, dependencies: [enabled], revertOnUpdate: true },
  );
}
